import React, { useState, useEffect, useRef } from "react";
import ReactFlow, { Background, Controls, MiniMap } from "reactflow";
import "reactflow/dist/style.css";
import PropTypes from "prop-types";
import { Alert, Box, Fab, Icon, Snackbar, TextField, Grid, CircularProgress } from "@mui/material";
import AddJobDialog from "components/AddJobDialog";
import MDBox from "components/MDBox";
import { addDepartment } from "services/api";
import DepartmentModel from "models/departmentModel";
import { addJob } from "services/api";
import JobModel from "models/jobModel";
import { deleteJob } from "services/api";
import { updateJobBranches } from "services/api";
import { getJobToBranch } from "services/api";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useAuth } from "context/authContext";
import {
  getCompanies,
  updateCompanyData,
  getValuesByCompanyId,
  updateValue,
  deleteValueById,
  addValue,
} from "services/api";
import { debounce } from "lodash"; // Import debounce from lodash
import { Card, CardContent, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ConfirmationDialog from "components/ConfirmationDialog";
import EmployeeAvatar from "components/EmployeeAvatar";
import { Edit } from "@mui/icons-material";
import CompanyLogo from "components/CompanyLogoAvatar";
import { updateCompanyDetails } from "services/api";
import { useTranslation } from "react-i18next";
import CompanyDetailsCard from "./components/companyview/index.js";
import SnackbarWrapper from "components/Snackbar";
import { updateCompanyInfoUsingAI } from "services/api.js";
import AiUpdateButton from "./components/AIIconButton/index.js";

const CompanyView = ({ company, setSelectedCompany, canEdit }) => {
  const { t } = useTranslation();
  const { employee, setCompanies } = useAuth();
  const [companyDataInitiatives, setCompanyData] = useState({});
  const [values, setValues] = useState([]);
  const [openAddValueDialog, setOpenAddValueDialog] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const snackbarRef = useRef();
  const [valuesLoading, setValuesLoading] = useState(false);
  const [isEditing, setIsEditing] = useState({ mission: false, vision: false, purpose: false });
  const [isSaved, setIsSaved] = useState({ mission: false, vision: false, purpose: false });
  const [loading, setLoading] = useState(false);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deletionId, setdeletionId] = useState();
  const [logoLoading, setLogoLoading] = useState(false);

  const handleUpdate = async (updatedData) => {
    try {
      const response = await updateCompanyData(employee.companyId, updatedData);
      snackbarRef.current?.showSnackbar(t("admin.strategicInfo.companyUpdateSuccess"), "success");
    } catch (error) {
      snackbarRef.current?.showSnackbar(t("admin.strategicInfo.companyUpdateFail"), "error");
    }
  };

  const handleDeleteById = async (id) => {
    setdeletionId(id);
    setConfirmationDialogOpen(true);
  };

  const handleSaveValue = async () => {
    if (!newValue.trim()) return;
    setSaveLoading(true);
    const newValueData = { companyId: employee.companyId, description: newValue };
    try {
      const response = await addValue(newValueData);
      setValues([...values, response]); // Update UI with the new value
      // setSnackbarMessage(t("admin.strategicInfo.valueAddSuccess"));
      // setSnackbarSeverity("success");
      snackbarRef.current?.showSnackbar(t("admin.strategicInfo.valueAddSuccess"), "success");
    } catch (error) {
      // setSnackbarMessage(t("admin.strategicInfo.valueAddFail"));
      // setSnackbarSeverity("error");
      snackbarRef.current?.showSnackbar(t("admin.strategicInfo.valueAddFail"), "error");
    }

    setSaveLoading(false);
    handleCloseDialog();
  };

  const handleBlur = (field, value) => {
    setCompanyData(companyDataInitiatives);
    handleUpdate(companyDataInitiatives); // Save update

    setIsEditing((prev) => ({ ...prev, [field]: false }));
    setIsSaved((prev) => ({ ...prev, [field]: true }));

    // Reset green border after 2 seconds
    setTimeout(() => {
      setIsSaved((prev) => ({ ...prev, [field]: false }));
    }, 2000);
  };

  const handleChange = (field, value) => {
    setIsEditing((prev) => ({ ...prev, [field]: true }));
    setCompanyData({ ...companyDataInitiatives, [field]: value });
  };

  useEffect(() => {
    const fetchData = async () => {
      // setLoading(true);
      setValuesLoading(true);
      try {
        if (company) {
          setCompanyData(company);

          const companyId = employee.companyId;
          const valsData = await getValuesByCompanyId(companyId);
          if (valsData != "error") setValues(valsData);
        }
      } finally {
        setValuesLoading(false);
      }
    };
    fetchData();
  }, [company]);

  const handleAddValueClick = () => {
    setOpenAddValueDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddValueDialog(false);
    setNewValue("");
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Validate file type (only images)
      if (!file.type.startsWith("image/")) {
        snackbarRef.current?.showSnackbar(t("admin.strategicInfo.companyUpdateWrongType"), "error");

        return;
      }

      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        snackbarRef.current?.showSnackbar(t("admin.strategicInfo.fileSizeError"), "error");
        return;
      }

      // Read the file as Base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result.split(",")[1]; // Remove prefix
        setLogoLoading(true);
        await updateCompany(base64String);
        setLogoLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateCompany = async (image) => {
    const newComp = { ...company, companyLogo: image };
    let res = await updateCompanyDetails(newComp);
    if (res != "error") {
      setSelectedCompany(res);
      setCompanies([res]);

      snackbarRef.current?.showSnackbar(t("admin.strategicInfo.logoChangedSuccess"), "success");
    }
  };

  const handleUpdateCompanyDataUsingAI = async () => {
    const res = await updateCompanyInfoUsingAI();
    if (res != "error") {
      const newComp = {
        ...company,
        vision: res.vision,
        mission: res.mission,
        purpose: res.purpose,
      }; // remove comment
      setCompanies([newComp]);
      setSelectedCompany(newComp);
      setValuesLoading(true);
      const valsData = await getValuesByCompanyId(company.companyId);
      if (valsData != "error") {
        setValues(valsData);
        setValuesLoading(false);
      }
      snackbarRef.current?.showSnackbar(res.comment, "ai");
    } else {
      snackbarRef.current?.showSnackbar(
        "The agent needs to rest for abit. please try again later",
        "error"
      );
    }
  };

  return (
    <MDBox p={3}>
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        // Responsive: Column on mobile, Row on tablet+
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        mb={3} // Move the bottom margin to the parent
      >
        <MDTypography
          variant="h4"
          // Remove individual margins so it centers perfectly with the button
          m={0}
          sx={{ lineHeight: 1 }} // optional: ensures tight vertical centering
        >
          {t("admin.strategicInfo.companyDetails")}
        </MDTypography>

        <AiUpdateButton handleAICall={handleUpdateCompanyDataUsingAI} />
      </MDBox>
      {logoLoading ? (
        <Box display="flex" justifyContent="center" p={1}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          {/*
          </label> */}
          {/* Wrap the entire logo + edit area inside the label */}
          <MDBox display="flex" justifyContent="center" mb={1} position="relative">
            {/* Only the logo box is wrapped in the label */}
            <label htmlFor="avatar-upload" style={{ cursor: "pointer", display: "inline-block" }}>
              <MDBox position="relative">
                {/* Company Logo */}
                <CompanyLogo photo={company?.companyLogo} />
                {/* Edit Icon */}
              </MDBox>
            </label>
          </MDBox>

          {/* Hidden input */}
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            id="avatar-upload"
            disabled={!canEdit}
            onChange={handleFileChange}
          />
        </>
      )}
      {/* Mission & Vision Cards */}

      <Grid container spacing={3} mb={4} alignItems="stretch">
        <Grid item xs={12} sm={4}>
          <CompanyDetailsCard
            title={t("admin.strategicInfo.mission")}
            value={companyDataInitiatives.mission}
            disabled={!canEdit}
            onChange={(e) => handleChange("mission", e.target.value)}
            onBlur={() => handleBlur("mission")}
            placeholder={t("admin.strategicInfo.enterNewMission")}
            isSaved={isSaved.mission}
            isEditing={isEditing.mission}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <CompanyDetailsCard
            title={t("admin.strategicInfo.vision")}
            value={companyDataInitiatives.vision}
            disabled={!canEdit}
            onChange={(e) => handleChange("vision", e.target.value)}
            onBlur={() => handleBlur("vision")}
            placeholder={t("admin.strategicInfo.enterNewVision")}
            isSaved={isSaved.vision}
            isEditing={isEditing.vision}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <CompanyDetailsCard
            title={t("admin.strategicInfo.purpose")}
            value={companyDataInitiatives.purpose}
            disabled={!canEdit}
            onChange={(e) => handleChange("purpose", e.target.value)}
            onBlur={() => handleBlur("purpose")}
            placeholder={t("admin.strategicInfo.enterNewPurpose")}
            isSaved={isSaved.purpose}
            isEditing={isEditing.purpose}
          />
        </Grid>
      </Grid>

      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />

      <Box mt={3} />
      <MDTypography variant="h4" mb={3} textAlign="center">
        {t("admin.strategicInfo.values")}
      </MDTypography>
      <Grid container spacing={2} alignItems="stretch">
        {!valuesLoading ? (
          values.map((value, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={value.id || index}>
              <Card
                sx={{
                  height: "100%", // Ensures all cards take up equal height
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  position: "relative",
                  transition: "0.3s",
                  "&:hover": { boxShadow: 5 },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    width: "85%",
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {value.description}
                  </Typography>
                </CardContent>

                {/* Delete Button - Positioned on the top right for better accessibility */}
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    color: "error.main",
                    "&:hover": { backgroundColor: "error.light" },
                  }}
                  disabled={!canEdit}
                  onClick={() => handleDeleteById(value.valueId)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Card>
            </Grid>
          ))
        ) : (
          <MDBox
            height="100%"
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={20} />
          </MDBox>
        )}
      </Grid>

      {/* </Box> */}

      {/* Add Value Button */}
      <Box display="flex" justifyContent="center" mt={2}>
        <IconButton color="primary" disabled={!canEdit} onClick={handleAddValueClick}>
          <AddCircleOutlineIcon fontSize="large" />
        </IconButton>
      </Box>

      <Dialog
        open={openAddValueDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            boxShadow: 10,
            p: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            textAlign: "center",
            fontSize: { xs: "1.2rem", sm: "1.5rem" },
          }}
        >
          {t("admin.strategicInfo.addNewValue")}
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            px: { xs: 1, sm: 3 },
            py: { xs: 1, sm: 2 },
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={6}
            label={t("admin.strategicInfo.valueDescription")}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            variant="outlined"
          />
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 },
          }}
        >
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            fullWidth
            sx={{
              borderRadius: 2,
              height: 40,
            }}
          >
            {t("admin.strategicInfo.cancel")}
          </Button>

          <Button
            onClick={handleSaveValue}
            color="primary"
            fullWidth
            disabled={saveLoading} // disable during loading
            sx={{
              borderRadius: 2,
              height: 40,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {saveLoading ? (
              <CircularProgress size={24} sx={{ position: "absolute" }} />
            ) : (
              t("admin.strategicInfo.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={async () => {
          try {
            if (!deletionId) return;
            const id = deletionId;
            await deleteValueById(id);
            setValues(values?.filter((value) => value.valueId !== id)); // Remove from UI
          } catch (error) {}
          setConfirmationDialogOpen(false);
        }}
      />
    </MDBox>
  );
};

CompanyView.propTypes = {
  company: PropTypes.object.isRequired,
  setSelectedCompany: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default CompanyView;
